import { useCallback, useEffect, useState } from 'react';

import {
  buildSimSwapEligibilityKey,
  type SimSwapVerificationState,
} from './simSwapReadiness';

export type ReplacementSimEligibility = {
  eligible: boolean;
  iccid: string;
  simStatus: string | null;
  dmaStatus: string | null;
  dmaMsisdn: string | null;
  ricaCompleted: boolean;
  assignedToAccount: boolean;
  blockers: string[];
};

type ReplacementSimAccount = {
  i_customer?: number | null;
  account_uuid?: string | null;
};

type UseSimSwapReplacementEligibilityArgs = {
  iccid: string;
  account: ReplacementSimAccount | null;
  canPerform: boolean;
  inputEnabled: boolean;
  verification?: SimSwapVerificationState | null;
  sourceSimIccid?: string | null;
  load: (iccid: string) => Promise<ReplacementSimEligibility>;
};

export const useSimSwapReplacementEligibility = ({
  iccid,
  account,
  canPerform,
  inputEnabled,
  verification,
  sourceSimIccid,
  load,
}: UseSimSwapReplacementEligibilityArgs) => {
  const [eligibility, setEligibility] =
    useState<ReplacementSimEligibility | null>(null);
  const [eligibilityKey, setEligibilityKey] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkNonce, setCheckNonce] = useState(0);

  const cleanedIccid = iccid.trim().replace(/\s+/g, '');
  const verificationStatus = verification?.status ?? null;
  const verificationRunUuid = verification?.verificationRunUuid ?? null;
  const contextKey = buildSimSwapEligibilityKey({
    accountId: account?.i_customer,
    accountUuid: account?.account_uuid,
    verificationRunUuid,
    verificationStatus,
    sourceSimIccid,
  });
  const currentKey = buildSimSwapEligibilityKey({
    accountId: account?.i_customer,
    accountUuid: account?.account_uuid,
    verificationRunUuid,
    verificationStatus,
    candidateIccid: cleanedIccid,
    sourceSimIccid,
  });

  useEffect(() => {
    if (
      !cleanedIccid ||
      !account?.i_customer ||
      !canPerform ||
      !inputEnabled
    ) {
      setEligibility(null);
      setEligibilityKey(null);
      setChecking(false);
      return;
    }

    if (!/^\d{18,22}$/.test(cleanedIccid)) {
      setEligibility({
        eligible: false,
        iccid: cleanedIccid,
        simStatus: null,
        dmaStatus: null,
        dmaMsisdn: null,
        ricaCompleted: false,
        assignedToAccount: false,
        blockers: ['Enter an ICCID between 18 and 22 digits'],
      });
      setEligibilityKey(currentKey);
      setChecking(false);
      return;
    }

    let cancelled = false;
    const requestKey = currentKey;
    setChecking(true);
    setEligibility(null);
    setEligibilityKey(null);
    const timer = window.setTimeout(async () => {
      try {
        const result = await load(cleanedIccid);
        if (!cancelled) {
          setEligibility(result);
          setEligibilityKey(requestKey);
        }
      } catch (error) {
        if (!cancelled) {
          setEligibility({
            eligible: false,
            iccid: cleanedIccid,
            simStatus: null,
            dmaStatus: null,
            dmaMsisdn: null,
            ricaCompleted: false,
            assignedToAccount: false,
            blockers: [
              error instanceof Error
                ? error.message
                : 'Could not validate replacement SIM',
            ],
          });
          setEligibilityKey(requestKey);
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    account?.account_uuid,
    account?.i_customer,
    canPerform,
    cleanedIccid,
    contextKey,
    currentKey,
    inputEnabled,
    load,
    checkNonce,
  ]);

  const recheck = useCallback(() => {
    setCheckNonce((value) => value + 1);
  }, []);

  return {
    eligibility,
    checking,
    isCurrent: eligibilityKey === currentKey,
    recheck,
  };
};
